import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useIsNewEntry } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  setPersistInlineSave,
  setTriggerInlineSave,
} from "src/redux/slices/generatorSlice";
/**
 * Custom hook to determine if a save operation is required for a specific section.
 *
 * @param sectionsClassName - The class name of the sections to monitor for changes.
 * @param inlineSaving - A boolean indicating if inline saving is enabled.
 * @param sectionIndex - The index of the section being monitored.
 * @returns A boolean indicating whether a save is required.
 */
const useIsSaveRequired = (
  sectionsClassName: string,
  inlineSaving: boolean,
  sectionIndex: number,
) => {
  const dispatch = useDispatch();
  const { lbeidOrPartId } = useParams();
  const isNewEntry = useIsNewEntry();
  const { noSaves } = useSelector(state => state.entrySlice);
  const { dynamicRowAdded, triggerInlineSave, forceTurnOffInlineSave } =
    useSelector(state => state.generatorSlice);
  const sectionUnsavedRowAdded = useSelector(
    state =>
      state.generatorSlice.lbentry.sections[sectionIndex]?.unsavedRowAdded,
  );
  const [isSaveRequired, setIsSaveRequired] = useState(() =>
    Boolean(sectionUnsavedRowAdded),
  );

  useEffect(() => {
    if (!inlineSaving) return;

    const handleInputChange = (event: any) => {
      const target = event.target as
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement;
      if (
        target.matches(
          `.${sectionsClassName} input, .${sectionsClassName} select, .${sectionsClassName} textarea`,
        )
      ) {
        setIsSaveRequired(true);

        if (isNewEntry) dispatch(setPersistInlineSave(sectionIndex));
      }
    };

    const container = document.querySelector(`.${sectionsClassName}`);
    container?.addEventListener("change", handleInputChange);

    return () => {
      container?.removeEventListener("change", handleInputChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [inlineSaving, sectionsClassName, isNewEntry]);

  useEffect(() => {
    setIsSaveRequired(Boolean(sectionUnsavedRowAdded));
  }, [
    noSaves,
    lbeidOrPartId,
    isNewEntry,
    forceTurnOffInlineSave,
    sectionIndex,
    sectionUnsavedRowAdded,
  ]);

  useEffect(() => {
    if (
      dynamicRowAdded === sectionIndex ||
      triggerInlineSave === sectionIndex
    ) {
      setIsSaveRequired(true);
      if (isNewEntry) dispatch(setPersistInlineSave(sectionIndex));
    }
    if (triggerInlineSave !== -1) {
      dispatch(setTriggerInlineSave(-1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [dynamicRowAdded, sectionIndex, triggerInlineSave, isNewEntry]);

  return isSaveRequired;
};

export default useIsSaveRequired;
